import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { History, ArrowRight, Filter, X, GitBranch, CheckCircle2, XCircle, RotateCcw, PenLine, Clock, Lock, Send, PenSquare } from "lucide-react";
import { useAuth } from "@clerk/react";
import { PersonaBadge } from "@/components/PersonaBadge";
import type { Persona } from "@/lib/orgContext";

interface AuditEntry {
  id: number;
  entityType: string;
  entityId: number;
  action: "create" | "update" | "delete" | "status_change" | "upload" | "remove" | "download";
  field: string | null;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  actorClerkUserId: string | null;
  actorName: string | null;
  actorPersonas: string[];
  createdAt: string;
}

interface ApprovalRecord {
  id: number;
  status: string;
  currentStage: string;
  submittedBy: string | null;
  submittedAt: string | null;
  completedAt: string | null;
  isAmendment: boolean;
}

interface ApprovalStep {
  id: number;
  approvalId: number;
  stage: string;
  approverEmail: string;
  decision: "approved" | "rejected" | "request_changes";
  comment: string | null;
  decidedAt: string;
}

interface SignerEntry {
  email: string;
  name?: string;
  status: "pending" | "sent" | "signed" | "declined";
  signedAt?: string;
  method?: string;
  typedName?: string;
}

interface SignatureRecord {
  id: number;
  approvalId: number;
  signers: SignerEntry[];
  status: string;
  sentAt: string | null;
  completedAt: string | null;
}

interface TimelineItem {
  kind: "audit" | "approval_submit" | "approval_decision" | "approval_complete" | "signature_sent" | "signer_event" | "contract_locked";
  sortKey: string;
  auditGroup?: AuditEntry[];
  approvalRecord?: ApprovalRecord;
  approvalStep?: ApprovalStep;
  signature?: SignatureRecord;
  signerEntry?: SignerEntry;
}

function tryParseJson<T>(s: string): T | null {
  try { return JSON.parse(s) as T; } catch { return null; }
}

const MONEY_FIELDS = new Set(["annualSalary", "signingBonus", "buyoutClause"]);
const DATE_FIELDS = new Set(["startDate", "endDate", "hireDate"]);

const STAGE_LABELS: Record<string, string> = {
  draft: "Draft",
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  compliance_review: "Compliance Review",
  ad_review: "AD Review",
  signature: "Signature",
  executed: "Executed",
};

function formatValue(field: string | null, value: string | null): string {
  if (value === null || value === "") return "—";
  if (field && MONEY_FIELDS.has(field)) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? `$${n.toLocaleString()}` : value;
  }
  if (field && DATE_FIELDS.has(field)) {
    try { return format(new Date(value), "MMM d, yyyy"); } catch { return value; }
  }
  return value;
}

function actionBadge(action: AuditEntry["action"]) {
  const map: Record<AuditEntry["action"], { label: string; cls: string }> = {
    create: { label: "Created", cls: "bg-green-500/10 text-green-700 border-green-200" },
    update: { label: "Updated", cls: "bg-blue-500/10 text-blue-700 border-blue-200" },
    delete: { label: "Deleted", cls: "bg-destructive/10 text-destructive border-destructive/30" },
    status_change: { label: "Status", cls: "bg-amber-500/10 text-amber-700 border-amber-200" },
    upload: { label: "Document Added", cls: "bg-purple-500/10 text-purple-700 border-purple-200" },
    remove: { label: "Document Removed", cls: "bg-orange-500/10 text-orange-700 border-orange-200" },
    download: { label: "Downloaded", cls: "bg-sky-500/10 text-sky-700 border-sky-200" },
  };
  const m = map[action] ?? { label: action, cls: "bg-muted text-muted-foreground border-border" };
  return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
}

function ApprovalSubmitRow({ record }: { record: ApprovalRecord }) {
  return (
    <div className="border rounded-lg p-3 bg-blue-50/40 border-blue-100">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-200 flex items-center gap-1">
            <GitBranch className="h-3 w-3" />
            {record.isAmendment ? "Re-submitted" : "Submitted for Approval"}
          </Badge>
          {record.submittedBy && (
            <span className="text-sm font-medium">{record.submittedBy}</span>
          )}
        </div>
        {record.submittedAt && (
          <span className="text-xs text-muted-foreground">
            {format(new Date(record.submittedAt), "MMM d, yyyy h:mm a")}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Routed through approval workflow{record.isAmendment ? " (amendment)" : ""}.
      </p>
    </div>
  );
}

function ApprovalDecisionRow({ step }: { step: ApprovalStep }) {
  const cfg = {
    approved: {
      icon: <CheckCircle2 className="h-3 w-3" />,
      label: "Approved",
      cls: "bg-green-500/10 text-green-700 border-green-200",
      rowCls: "bg-green-50/40 border-green-100",
    },
    rejected: {
      icon: <XCircle className="h-3 w-3" />,
      label: "Rejected",
      cls: "bg-destructive/10 text-destructive border-destructive/30",
      rowCls: "bg-red-50/40 border-red-100",
    },
    request_changes: {
      icon: <RotateCcw className="h-3 w-3" />,
      label: "Changes Requested",
      cls: "bg-amber-500/10 text-amber-700 border-amber-200",
      rowCls: "bg-amber-50/40 border-amber-100",
    },
  }[step.decision];

  return (
    <div className={`border rounded-lg p-3 ${cfg.rowCls}`}>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`flex items-center gap-1 ${cfg.cls}`}>
            {cfg.icon} {cfg.label}
          </Badge>
          <span className="text-sm font-medium">{step.approverEmail}</span>
          <span className="text-xs text-muted-foreground">· {STAGE_LABELS[step.stage] ?? step.stage}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {format(new Date(step.decidedAt), "MMM d, yyyy h:mm a")}
        </span>
      </div>
      {step.comment && (
        <p className="text-xs text-muted-foreground italic mt-1">"{step.comment}"</p>
      )}
    </div>
  );
}

function ApprovalCompleteRow({ record }: { record: ApprovalRecord }) {
  if (record.status === "in_progress") return null;

  const cfg: Record<string, { icon: React.ReactNode; label: string; cls: string; rowCls: string }> = {
    executed: {
      icon: <CheckCircle2 className="h-3 w-3" />,
      label: "Contract Executed",
      cls: "bg-green-500/10 text-green-700 border-green-200",
      rowCls: "bg-green-50/40 border-green-100",
    },
    rejected: {
      icon: <XCircle className="h-3 w-3" />,
      label: "Approval Rejected",
      cls: "bg-destructive/10 text-destructive border-destructive/30",
      rowCls: "bg-red-50/40 border-red-100",
    },
    returned: {
      icon: <RotateCcw className="h-3 w-3" />,
      label: "Returned for Changes",
      cls: "bg-amber-500/10 text-amber-700 border-amber-200",
      rowCls: "bg-amber-50/40 border-amber-100",
    },
    cancelled: {
      icon: <Clock className="h-3 w-3" />,
      label: "Approval Cancelled",
      cls: "bg-muted text-muted-foreground border-border",
      rowCls: "bg-muted/30 border-border",
    },
  };

  const c = cfg[record.status];
  if (!c || !record.completedAt) return null;

  return (
    <div className={`border rounded-lg p-3 ${c.rowCls}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Badge variant="outline" className={`flex items-center gap-1 ${c.cls}`}>
          {c.icon} {c.label}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {format(new Date(record.completedAt), "MMM d, yyyy h:mm a")}
        </span>
      </div>
      {record.status === "executed" && (
        <p className="text-xs text-muted-foreground mt-1">
          All approvals obtained — contract is now fully executed.
        </p>
      )}
    </div>
  );
}

export function ContractHistory({ contractId }: { contractId: number }) {
  const { getToken } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [approvalHistory, setApprovalHistory] = useState<ApprovalRecord[]>([]);
  const [approvalSteps, setApprovalSteps] = useState<ApprovalStep[]>([]);
  const [signatureRecord, setSignatureRecord] = useState<SignatureRecord | null>(null);
  const [currentApproval, setCurrentApproval] = useState<ApprovalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState<string>("");
  const [fieldFilter, setFieldFilter] = useState<string>("__all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("__all");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const token = await getToken();
        const params = new URLSearchParams();
        if (actorFilter.trim()) params.set("actor", actorFilter.trim());
        if (fieldFilter && fieldFilter !== "__all") params.set("field", fieldFilter);
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo + "T23:59:59.999Z");
        const [auditRes, approvalRes] = await Promise.all([
          fetch(
            `/api/contracts/${contractId}/history${params.toString() ? `?${params.toString()}` : ""}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          fetch(`/api/contracts/${contractId}/approval`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!cancelled && auditRes.ok) {
          setEntries(await auditRes.json() as AuditEntry[]);
        }
        if (!cancelled && approvalRes.ok) {
          const data = await approvalRes.json() as {
            approval: ApprovalRecord | null;
            steps: ApprovalStep[];
            history: ApprovalRecord[];
            signature: SignatureRecord | null;
          };
          setApprovalSteps(data.steps ?? []);
          setApprovalHistory(data.history ?? (data.approval ? [data.approval] : []));
          setSignatureRecord(data.signature ?? null);
          setCurrentApproval(data.approval);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [contractId, actorFilter, fieldFilter, dateFrom, dateTo, getToken]);

  const availableFields = useMemo(() => {
    const set = new Set<string>();
    entries.forEach((e) => { if (e.field) set.add(e.field); });
    return Array.from(set).sort();
  }, [entries]);

  const auditGrouped = useMemo(() => {
    const groups = new Map<string, AuditEntry[]>();
    for (const e of entries) {
      const key = `${e.actorClerkUserId ?? "system"}|${new Date(e.createdAt).toISOString().slice(0, 16)}|${e.action}`;
      const arr = groups.get(key) ?? [];
      arr.push(e);
      groups.set(key, arr);
    }
    return Array.from(groups.values());
  }, [entries]);

  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    if (typeFilter === "__all" || typeFilter === "changes") {
      for (const group of auditGrouped) {
        const head = group[0];
        items.push({ kind: "audit", sortKey: head.createdAt, auditGroup: group });
      }
    }

    if (typeFilter === "__all" || typeFilter === "approval") {
      for (const record of approvalHistory) {
        if (record.submittedAt) {
          items.push({ kind: "approval_submit", sortKey: record.submittedAt, approvalRecord: record });
        }
        if (record.completedAt && record.status !== "in_progress") {
          items.push({ kind: "approval_complete", sortKey: record.completedAt, approvalRecord: record });
        }
      }
      for (const step of approvalSteps) {
        if (step.decidedAt) {
          items.push({ kind: "approval_decision", sortKey: step.decidedAt, approvalStep: step });
        }
      }

      // Signature lifecycle events — only from the current active approval's envelope
      if (signatureRecord && currentApproval) {
        // "Contract locked — entered signature stage" event
        // Derive timestamp: use the latest review-stage decision timestamp, or approval.submittedAt as fallback
        const lastDecision = approvalSteps
          .filter((s) => s.stage !== "signature" && s.decidedAt)
          .sort((a, b) => new Date(b.decidedAt).getTime() - new Date(a.decidedAt).getTime())[0];
        const lockedAt = lastDecision?.decidedAt ?? currentApproval.submittedAt;
        if (lockedAt) {
          items.push({
            kind: "contract_locked",
            sortKey: lockedAt,
            approvalRecord: currentApproval,
            signature: signatureRecord,
          });
        }

        // "Sent for e-signature" event
        if (signatureRecord.sentAt) {
          items.push({
            kind: "signature_sent",
            sortKey: signatureRecord.sentAt,
            signature: signatureRecord,
          });
        }

        // Per-signer completion events
        for (const signer of signatureRecord.signers ?? []) {
          if ((signer.status === "signed" || signer.status === "declined") && signer.signedAt) {
            items.push({
              kind: "signer_event",
              sortKey: signer.signedAt,
              signerEntry: signer,
              signature: signatureRecord,
            });
          }
        }
      }
    }

    return items.sort((a, b) => new Date(b.sortKey).getTime() - new Date(a.sortKey).getTime());
  }, [auditGrouped, approvalHistory, approvalSteps, signatureRecord, currentApproval, typeFilter]);

  const hasFilters = actorFilter || (fieldFilter && fieldFilter !== "__all") || dateFrom || dateTo || typeFilter !== "__all";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <History className="h-5 w-5 text-muted-foreground" /> Audit Trail
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-muted-foreground">Actor</label>
            <Input
              data-testid="audit-filter-actor"
              placeholder="Filter by name…"
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
            />
          </div>
          <div className="min-w-[160px]">
            <label className="text-xs text-muted-foreground">Type</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger><SelectValue placeholder="All events" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All events</SelectItem>
                <SelectItem value="changes">Field changes</SelectItem>
                <SelectItem value="approval">Approval events</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <label className="text-xs text-muted-foreground">Field</label>
            <Select value={fieldFilter} onValueChange={setFieldFilter}>
              <SelectTrigger data-testid="audit-filter-field"><SelectValue placeholder="All fields" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All fields</SelectItem>
                {availableFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">From</label>
            <Input data-testid="audit-filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To</label>
            <Input data-testid="audit-filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setActorFilter(""); setFieldFilter("__all"); setDateFrom(""); setDateTo(""); setTypeFilter("__all"); }}>
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center">Loading history…</div>
        ) : timeline.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center" data-testid="audit-empty">
            <Filter className="h-5 w-5 mx-auto mb-2 opacity-50" />
            {hasFilters ? "No entries match these filters." : "No history yet. Changes will appear here."}
          </div>
        ) : (
          <div className="space-y-3" data-testid="audit-history-list">
            {timeline.map((item, idx) => {
              if (item.kind === "approval_submit" && item.approvalRecord) {
                return <ApprovalSubmitRow key={`submit-${item.approvalRecord.id}`} record={item.approvalRecord} />;
              }
              if (item.kind === "approval_decision" && item.approvalStep) {
                return <ApprovalDecisionRow key={`step-${item.approvalStep.id}`} step={item.approvalStep} />;
              }
              if (item.kind === "approval_complete" && item.approvalRecord) {
                return <ApprovalCompleteRow key={`complete-${item.approvalRecord.id}-${idx}`} record={item.approvalRecord} />;
              }
              if (item.kind === "contract_locked" && item.approvalRecord) {
                return (
                  <div key={`locked-${item.approvalRecord.id}`} className="border rounded-lg p-3 bg-amber-50/40 border-amber-100">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-200 flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Contract Locked — Entered Signature Stage
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(item.sortKey), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      All review stages approved. Contract is locked for edits; pending e-signature collection.
                    </p>
                  </div>
                );
              }
              if (item.kind === "signature_sent" && item.signature) {
                return (
                  <div key={`sig-sent-${item.signature.id}`} className="border rounded-lg p-3 bg-blue-50/40 border-blue-100">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-200 flex items-center gap-1">
                        <Send className="h-3 w-3" /> Sent for Signature
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(item.sortKey), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Signature envelope sent to {item.signature.signers.length} signer{item.signature.signers.length !== 1 ? "s" : ""}.
                    </p>
                  </div>
                );
              }
              if (item.kind === "signer_event" && item.signerEntry) {
                const signer = item.signerEntry;
                const isSigned = signer.status === "signed";
                return (
                  <div key={`signer-${signer.email}-${item.sortKey}`} className={`border rounded-lg p-3 ${isSigned ? "bg-green-50/40 border-green-100" : "bg-red-50/40 border-red-100"}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Badge variant="outline" className={`flex items-center gap-1 ${isSigned ? "bg-green-500/10 text-green-700 border-green-200" : "bg-destructive/10 text-destructive border-destructive/30"}`}>
                        {isSigned ? <PenSquare className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        {isSigned ? "Signed" : "Declined"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(item.sortKey), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {signer.name ?? signer.email}
                      {isSigned && signer.method ? ` · ${signer.method === "in_app_draw" ? "In-app drawn" : signer.method === "in_app_typed" ? "In-app typed" : "External"}` : ""}
                      {isSigned && signer.typedName ? ` · "${signer.typedName}"` : ""}
                    </p>
                  </div>
                );
              }
              if (item.kind === "audit" && item.auditGroup) {
                const group = item.auditGroup;
                const head = group[0];
                return (
                  <div key={`audit-${head.id}`} className="border rounded-lg p-3 bg-muted/30" data-testid="audit-history-group">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {actionBadge(head.action)}
                        {head.action === "upload" || head.action === "download" || head.action === "remove" ? (
                          <PenLine className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : null}
                        <span className="text-sm font-medium">{head.actorName ?? "Unknown actor"}</span>
                        {head.actorPersonas && head.actorPersonas.length > 0 && head.actorPersonas.map((p) => (
                          <PersonaBadge key={p} persona={p as Persona} size="xs" />
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground" title={new Date(head.createdAt).toISOString()}>
                        {format(new Date(head.createdAt), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                    {group.some((e) => e.field) ? (
                      <div className="space-y-1.5">
                        {group.filter((e) => e.field).map((e) => (
                          <div key={e.id} className="text-sm flex items-center gap-2 flex-wrap" data-testid="audit-field-row">
                            <span className="font-medium text-muted-foreground min-w-[140px]">{e.fieldLabel ?? e.field}</span>
                            <span className="line-through text-muted-foreground">{formatValue(e.field, e.oldValue)}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium">{formatValue(e.field, e.newValue)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground italic">
                        {head.action === "upload" && head.newValue ? `Uploaded: ${tryParseJson<{ fileName?: string }>(head.newValue)?.fileName ?? "document"}` :
                         head.action === "remove" && head.oldValue ? `Removed: ${tryParseJson<{ fileName?: string }>(head.oldValue)?.fileName ?? "document"}` :
                         head.action === "download" && head.newValue ? (() => {
                           const d = tryParseJson<{ type?: string; fileName?: string; documentCount?: number }>(head.newValue);
                           if (d?.type === "zip") return `Downloaded all documents (${d.documentCount ?? "?"} files)`;
                           if (d?.fileName) return `Downloaded: ${d.fileName}`;
                           return "Document downloaded";
                         })() :
                         head.action === "delete" ? "Record deleted" :
                         head.action === "create" ? "Record created" :
                         "Change recorded"}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
